import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { FaHome, FaCheckCircle, FaClock, FaTimesCircle, FaSearch, FaPlus } from 'react-icons/fa';
import AdminSidebar from '../components/AdminSidebar';
import QuoteModal from '../components/QuoteModal';
import QuoteCard from '../components/QuoteCard';
import QuoteSummary from '../components/QuoteSummary';
import html2canvas from 'html2canvas';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteToDownload, setQuoteToDownload] = useState(null);
  const quoteSummaryRef = useRef(null);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchQuotes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('full_house_quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchQuotes();
  }, [checkAuth, fetchQuotes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('full_house_quotes')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const downloadQuote = async (quote) => {
    if (!quoteSummaryRef.current || !quote) return;

    try {
      const canvas = await html2canvas(quoteSummaryRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const link = document.createElement('a');
      link.download = `Cotizacion_${quote.full_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      setQuoteToDownload(null);
    } catch (error) {
      console.error('Error al generar imagen:', error);
      alert('No se pudo generar la imagen. Por favor, intenta nuevamente.');
    }
  };

  useEffect(() => {
    if (quoteToDownload && quoteSummaryRef.current) {
      setTimeout(() => {
        downloadQuote(quoteToDownload);
      }, 100);
    }
  }, [quoteToDownload]);

  // Filtrar cotizaciones del mes actual
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  const monthlyQuotes = quotes.filter(q => {
    const createdDate = new Date(q.created_at);
    return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
  });

  // Obtener nombre del mes actual
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const currentMonthName = monthNames[currentMonth];

  const filteredQuotes = quotes.filter(quote => {
    const matchesFilter = filter === 'all' || quote.status === filter;
    const matchesSearch = 
      quote.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.phone.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuotes = filteredQuotes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FaClock, label: 'Pendiente' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', icon: FaCheckCircle, label: 'Confirmada' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: FaTimesCircle, label: 'Cancelada' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`${config.bg} ${config.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const calculateNights = (checkin, checkout) => {
    const checkinDate = new Date(checkin);
    const checkoutDate = new Date(checkout);
    const timeDiff = checkoutDate.getTime() - checkinDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando cotizaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <AdminSidebar />
      
      <div className="flex-1 w-full md:w-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-playfair font-bold text-gray-900">
              Cotizaciones de Casa Completa
            </h1>
            <p className="text-xs md:text-sm text-gray-600 mt-1">Gestiona las solicitudes de alquiler completo</p>
          </div>
        </div>

        <main className="px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Month Indicator */}
        <div className="mb-4 md:mb-6">
          <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2">
            📊 Estadísticas de {currentMonthName} {currentYear}
          </h2>
          <p className="text-xs md:text-sm text-gray-600 mt-1">Cotizaciones del mes en curso</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
                <p className="text-xs md:text-sm text-gray-600 mb-1">Total</p>
                <p className="text-xl md:text-2xl font-bold text-gray-900">{monthlyQuotes.length}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaHome className="text-blue-600" size={16} />
              </div>
            </div>
          </div>
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
                <p className="text-xs md:text-sm text-gray-600 mb-1">Pendientes</p>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">
                  {monthlyQuotes.filter(q => q.status === 'pending').length}
                </p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaClock className="text-yellow-600" size={16} />
              </div>
            </div>
          </div>
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
                <p className="text-xs md:text-sm text-gray-600 mb-1">Confirmadas</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {monthlyQuotes.filter(q => q.status === 'confirmed').length}
                </p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaCheckCircle className="text-green-600" size={16} />
              </div>
            </div>
          </div>
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
                <p className="text-xs md:text-sm text-gray-600 mb-1">Canceladas</p>
                <p className="text-xl md:text-2xl font-bold text-red-600">
                  {monthlyQuotes.filter(q => q.status === 'cancelled').length}
                </p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FaTimesCircle className="text-red-600" size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col gap-3 md:gap-4">
            <div className="w-full">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'confirmed', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-colors text-xs md:text-sm ${
                    filter === status
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status === 'all' ? 'Todas' : status === 'pending' ? 'Pendientes' : status === 'confirmed' ? 'Confirmadas' : 'Canceladas'}
                </button>
              ))}
              <button
                onClick={() => {
                  setEditingQuote(null);
                  setShowQuoteModal(true);
                }}
                className="flex items-center gap-2 bg-blue-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-xs md:text-sm"
              >
                <FaPlus size={12} />
                <span className="hidden sm:inline">Nueva Cotización</span>
                <span className="sm:hidden">Nueva</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quotes List */}
        <div className="space-y-4">
          {currentQuotes.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <FaHome className="mx-auto text-gray-300 mb-4" size={64} />
              <p className="text-gray-500 text-lg">No se encontraron cotizaciones</p>
            </div>
          ) : (
            currentQuotes.map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                calculateNights={calculateNights}
                getStatusBadge={getStatusBadge}
                onUpdateStatus={updateStatus}
                onDownload={(quote) => setQuoteToDownload(quote)}
                onEdit={(quote) => {
                  setEditingQuote(quote);
                  setShowQuoteModal(true);
                }}
              />
            ))
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4 mt-4">
            <p className="text-xs md:text-sm text-gray-600 text-center sm:text-left">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredQuotes.length)} de {filteredQuotes.length} cotizaciones
            </p>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 md:px-4 py-1.5 md:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-xs md:text-sm"
              >
                Anterior
              </button>
              <span className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-gray-600 font-medium whitespace-nowrap">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 md:px-4 py-1.5 md:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-xs md:text-sm"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Quote Modal */}
      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => {
          setShowQuoteModal(false);
          setEditingQuote(null);
        }}
        existingQuote={editingQuote}
        onSuccess={fetchQuotes}
      />

      {/* Hidden component for rendering summary to image */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }}>
        <QuoteSummary ref={quoteSummaryRef} quote={quoteToDownload} />
      </div>
      </div>
    </div>
  );
};

export default AdminQuotes;
